if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      stickyActionsAbortController = undefined;
      stickyActionsMediaQuery = undefined;
      stickyActionsMediaQueryListener = undefined;
      stickyActionsResizeObserver = undefined;
      stickyActionsUpdateFrame = undefined;
      stickyActionsPlacementPending = false;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      productInfoRequest = undefined;
      productInfoRequestSequence = 0;
      productInfoBusyValue = undefined;
      productVariantSelectionUnresolved = false;
      variantIntentAbortController = undefined;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      get productContainer() {
        return this.querySelector('.product');
      }

      get productInfoContainer() {
        return this.querySelector('.product__info-container');
      }

      get productInfoWrapper() {
        return this.querySelector('.product__info-wrapper');
      }

      get productBlocks() {
        return this.querySelector('[data-product-right-column]');
      }

      get productConfiguratorScroll() {
        return this.querySelector('[data-product-config-scroll]');
      }

      get productConfiguratorTrack() {
        return this.querySelector('[data-product-config-track]') || this.productConfiguratorScroll;
      }

      get productStickyActions() {
        return this.querySelector('[data-product-sticky-actions]');
      }

      get productResponsiveTitleSlot() {
        return this.querySelector('[data-product-responsive-title-slot]');
      }

      get productResponsiveTitle() {
        return this.querySelector('[data-product-responsive-title]');
      }

      get productResponsiveTitleAnchor() {
        return this.querySelector('[data-product-responsive-title-anchor]');
      }

      get productResponsiveDetailsSlot() {
        return this.querySelector('[data-product-responsive-details-slot]');
      }

      get productResponsiveDetailsBlocks() {
        return Array.from(this.querySelectorAll('[data-product-responsive-details]'));
      }

      get productConfiguratorStart() {
        return this.querySelector('[data-product-configurator-step]');
      }

      get productMobileCtaEnd() {
        return this.querySelector('[data-product-mobile-cta-end]');
      }

      get productMobileCtaBlock() {
        return this.productStickyActions?.querySelector('.product__block--configurator-actions');
      }

      get productMediaContainer() {
        return this.querySelector('.product__media-container');
      }

      get productMediaViewer() {
        return (
          this.querySelector('.product__media-gallery-viewer-wrap') ||
          this.querySelector('.product__media-gallery-viewer') ||
          this.productMediaContainer
        );
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }

        this.initProductStickyActions();
        this.initProductVariantIntent();
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });

        this.queueProductStickyActionsUpdate();
      }

      initProductStickyActions() {
        const actions = this.productStickyActions;
        const blocks = this.productBlocks;
        if (!actions || !blocks) return;

        this.teardownProductStickyActions();
        this.stickyActionsAbortController = new AbortController();
        const { signal } = this.stickyActionsAbortController;
        const queueUpdate = this.queueProductStickyActionsUpdate.bind(this);

        window.addEventListener('scroll', queueUpdate, { passive: true, signal });
        window.addEventListener('resize', queueUpdate, { passive: true, signal });
        window.addEventListener('orientationchange', queueUpdate, { passive: true, signal });
        document.addEventListener('todaymerch:cookie-banner-layout', queueUpdate, { signal });
        this.addEventListener('focusin', queueUpdate, { signal });
        this.addEventListener('focusout', queueUpdate, { signal });
        this.addEventListener('click', queueUpdate, { signal });
        this.addEventListener('click', this.handleMobilePersonalizeClick.bind(this), { signal });
        window.visualViewport?.addEventListener('resize', queueUpdate, { passive: true, signal });
        window.visualViewport?.addEventListener('scroll', queueUpdate, { passive: true, signal });

        this.stickyActionsMediaQuery = window.matchMedia?.('(min-width: 1100px)');
        this.stickyActionsMediaQueryListener = queueUpdate;

        if (this.stickyActionsMediaQuery?.addEventListener) {
          this.stickyActionsMediaQuery.addEventListener('change', this.stickyActionsMediaQueryListener, { signal });
        } else if (this.stickyActionsMediaQuery?.addListener) {
          this.stickyActionsMediaQuery.addListener(this.stickyActionsMediaQueryListener);
        }

        if ('ResizeObserver' in window) {
          this.stickyActionsResizeObserver = new ResizeObserver(queueUpdate);
          [
            this.productContainer,
            this.productMediaWrapper,
            this.productInfoWrapper,
            this.productInfoContainer,
            this.productBlocks,
            this.productConfiguratorScroll,
            this.productConfiguratorTrack,
            this.productStickyActions,
            this.productMobileCtaBlock,
            this.productConfiguratorStart,
            this.productMobileCtaEnd,
            this.productResponsiveTitle,
            this.productResponsiveTitleSlot,
            this.productResponsiveDetailsSlot,
            ...this.productResponsiveDetailsBlocks,
            this.productMediaContainer,
            this.productMediaViewer,
          ]
            .filter(Boolean)
            .forEach((element) => this.stickyActionsResizeObserver.observe(element));
        }

        this.queueProductStickyActionsUpdate();
      }

      teardownProductStickyActions() {
        this.stickyActionsAbortController?.abort();
        this.stickyActionsAbortController = undefined;

        if (this.stickyActionsMediaQuery?.removeEventListener && this.stickyActionsMediaQueryListener) {
          this.stickyActionsMediaQuery.removeEventListener('change', this.stickyActionsMediaQueryListener);
        } else if (this.stickyActionsMediaQuery?.removeListener && this.stickyActionsMediaQueryListener) {
          this.stickyActionsMediaQuery.removeListener(this.stickyActionsMediaQueryListener);
        }

        this.stickyActionsMediaQuery = undefined;
        this.stickyActionsMediaQueryListener = undefined;

        this.stickyActionsResizeObserver?.disconnect();
        this.stickyActionsResizeObserver = undefined;

        if (this.stickyActionsUpdateFrame) {
          window.cancelAnimationFrame(this.stickyActionsUpdateFrame);
          this.stickyActionsUpdateFrame = undefined;
        }
        this.stickyActionsPlacementPending = false;

        this.resetProductStickyActions();
      }

      isDesktopProductStickyActionsEnabled() {
        return this.stickyActionsMediaQuery?.matches ?? window.matchMedia?.('(min-width: 1100px)')?.matches === true;
      }

      queueProductStickyActionsUpdate(event) {
        // Scroll changes the sticky progress, never the responsive placement.
        // Keep a layout request if a resize/variant update shares this frame.
        if (event?.type !== 'scroll') this.stickyActionsPlacementPending = true;
        if (this.stickyActionsUpdateFrame) return;

        this.stickyActionsUpdateFrame = window.requestAnimationFrame(() => {
          this.stickyActionsUpdateFrame = undefined;
          const updatePlacement = this.stickyActionsPlacementPending;
          this.stickyActionsPlacementPending = false;
          this.updateProductStickyActions({ updatePlacement });
        });
      }

      setProductStickyStyle(element, property, value = '') {
        if (!element || element.style.getPropertyValue(property) === value) return;
        if (value) element.style.setProperty(property, value);
        else element.style.removeProperty(property);
      }

      toggleProductStickyClass(element, name, enabled) {
        if (element && element.classList.contains(name) !== enabled) element.classList.toggle(name, enabled);
      }

      resetDesktopProductStickyActions() {
        const product = this.productContainer;
        const infoWrapper = this.productInfoWrapper;
        const infoContainer = this.productInfoContainer;
        const blocks = this.productBlocks;
        const actions = this.productStickyActions;

        this.toggleProductStickyClass(product, 'is-config-scroll-stage', false);
        this.setProductStickyStyle(product, '--product-config-stage-height');
        this.setProductStickyStyle(infoWrapper, '--product-config-stage-height');
        this.setProductStickyStyle(infoContainer, '--product-info-start-offset');
        this.setProductStickyStyle(infoContainer, '--product-right-column-height');

        this.toggleProductStickyClass(blocks, 'is-config-scroll-stage', false);
        this.toggleProductStickyClass(blocks, 'is-config-scroll-complete', false);
        this.setProductStickyStyle(blocks, '--product-config-track-offset');

        this.toggleProductStickyClass(actions, 'is-fixed', false);
        this.toggleProductStickyClass(actions, 'is-anchored', false);
      }

      resetMobileProductStickyActions() {
        const product = this.productContainer;
        const actionBlock = this.productMobileCtaBlock;

        this.toggleProductStickyClass(product, 'has-mobile-contextual-cta', false);
        this.setProductStickyStyle(product, '--pdp-mobile-cta-height');
        this.toggleProductStickyClass(actionBlock, 'is-mobile-contextual', false);
        if (actionBlock?.hasAttribute('data-mobile-cta-state')) actionBlock.removeAttribute('data-mobile-cta-state');
      }

      resetProductStickyActions() {
        this.resetDesktopProductStickyActions();
        this.resetMobileProductStickyActions();
      }

      updateResponsiveTitlePlacement() {
        const product = this.productContainer;
        const slot = this.productResponsiveTitleSlot;
        const title = this.productResponsiveTitle;
        const anchor = this.productResponsiveTitleAnchor;
        const useResponsiveSlot = window.matchMedia?.('(max-width: 1099.98px)')?.matches === true;

        if (!product || !slot || !title || !anchor) return;

        if (useResponsiveSlot) {
          if (title.parentElement !== slot) slot.append(title);
          product.classList.add('has-responsive-title');
          return;
        }

        if (title.previousElementSibling !== anchor) anchor.after(title);
        product.classList.remove('has-responsive-title');
      }

      updateResponsiveDetailsPlacement() {
        const slot = this.productResponsiveDetailsSlot;
        const detailsBlocks = this.productResponsiveDetailsBlocks;
        const useResponsiveSlot = window.matchMedia?.('(max-width: 1099.98px)')?.matches === true;

        if (!slot || !detailsBlocks.length) return;

        const anchors = Array.from(this.querySelectorAll('[data-product-responsive-details-anchor]'));

        detailsBlocks.forEach((details) => {
          const blockId = details.dataset.productResponsiveDetails;
          const anchor = anchors.find((element) => element.dataset.productResponsiveDetailsAnchor === blockId);
          if (!anchor) return;

          if (useResponsiveSlot) {
            if (details.parentElement !== slot) slot.append(details);
            return;
          }

          if (details.previousElementSibling !== anchor) anchor.after(details);
        });
      }

      isMobileProductOverlayOpen() {
        const overlays = document.querySelectorAll(
          [
            'dialog[open]',
            '[role="dialog"][open]',
            '[aria-modal="true"][open]',
            'basic-modal[open]',
            'basic-modal[active]',
            '.drawer[active]',
            '.modal[active]',
            '.drawer.active',
            '.drawer.is-open',
            '.modal.active',
            '.modal.is-open',
          ].join(', '),
        );

        return Array.from(overlays).some((overlay) => overlay.getClientRects().length > 0);
      }

      hasTextEntryFocus() {
        const activeElement = document.activeElement;
        if (!(activeElement instanceof HTMLElement)) return false;

        return activeElement.matches(
          [
            'textarea:not([disabled])',
            'select:not([disabled])',
            'input:not([disabled]):not([type])',
            'input[type="text"]:not([disabled])',
            'input[type="email"]:not([disabled])',
            'input[type="tel"]:not([disabled])',
            'input[type="url"]:not([disabled])',
            'input[type="search"]:not([disabled])',
            'input[type="number"]:not([disabled])',
            'input[type="password"]:not([disabled])',
            'input[type="date"]:not([disabled])',
            'input[type="time"]:not([disabled])',
            '[contenteditable="true"]',
          ].join(', '),
        );
      }

      handleMobilePersonalizeClick(event) {
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('[data-product-personalize-link]');
        if (!link || !this.contains(link)) return;
        const marking = this.querySelector('.product__block--configurator-step--marking');
        if (!marking) return;

        event.preventDefault();
        const focusTarget = marking.querySelector('[data-role="trigger"], .product-marking-selector__unavailable-header');
        if (focusTarget) {
          if (!focusTarget.matches('button, a, input, [tabindex]')) focusTarget.tabIndex = -1;
          focusTarget.focus({ preventScroll: true });
        }
        marking.scrollIntoView({
          block: 'start',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      updateMobileProductStickyActions() {
        const product = this.productContainer;
        const actionBlock = this.productMobileCtaBlock;
        const start = this.productConfiguratorStart;
        const end = this.productMobileCtaEnd;
        const isMobile = window.matchMedia?.('(max-width: 1099.98px)')?.matches === true;

        if (!product || !actionBlock || !start || !end || !isMobile) {
          this.resetMobileProductStickyActions();
          return;
        }

        const viewportHeight =
          Math.round(window.visualViewport?.height || 0) ||
          window.innerHeight ||
          document.documentElement.clientHeight;
        const actionHeight = Math.ceil(actionBlock.getBoundingClientRect().height);

        if (viewportHeight <= 0 || actionHeight <= 0) {
          this.resetMobileProductStickyActions();
          return;
        }

        const startRect = start.getBoundingClientRect();
        const endRect = end.getBoundingClientRect();
        const productRect = product.getBoundingClientRect();
        // The reassurance is hidden while fixed: using the whole block's height
        // makes the exit threshold alternate on every ResizeObserver callback.
        const buttonHeight = Math.ceil(actionBlock.querySelector('[data-role="quote-submit"]')?.getBoundingClientRect().height || 0);
        const exitOffset = buttonHeight + 32;
        const cookieClearance = Math.max(0, parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--cookie-consent-bottom-clearance'),
        ) || 0);
        const hasEnteredConfigurator = startRect.top <= viewportHeight / 2;
        const hasReachedInlineAction = endRect.top <= viewportHeight - cookieClearance - exitOffset;
        const isInsideProduct = productRect.bottom > exitOffset && productRect.top < viewportHeight;
        const layoutViewportHeight = document.documentElement.clientHeight || window.innerHeight || viewportHeight;
        const isVirtualKeyboardOpen =
          this.hasTextEntryFocus() && layoutViewportHeight - viewportHeight > Math.min(140, layoutViewportHeight * 0.18);
        const shouldFix =
          hasEnteredConfigurator &&
          !hasReachedInlineAction &&
          isInsideProduct &&
          !isVirtualKeyboardOpen &&
          !this.isMobileProductOverlayOpen();

        // Measure the current layout together before changing its fixed state.
        this.setProductStickyStyle(product, '--pdp-mobile-cta-height', `${actionHeight}px`);
        this.toggleProductStickyClass(product, 'has-mobile-contextual-cta', shouldFix);
        this.toggleProductStickyClass(actionBlock, 'is-mobile-contextual', shouldFix);
        const state = shouldFix ? 'fixed' : 'flow';
        if (actionBlock.dataset.mobileCtaState !== state) actionBlock.dataset.mobileCtaState = state;
      }

      updateProductStickyActions({ updatePlacement = true } = {}) {
        if (updatePlacement) {
          this.updateResponsiveTitlePlacement();
          this.updateResponsiveDetailsPlacement();
        }

        if (!this.isDesktopProductStickyActionsEnabled()) {
          this.resetDesktopProductStickyActions();
          this.updateMobileProductStickyActions();
          return;
        }

        this.resetMobileProductStickyActions();

        const product = this.productContainer;
        const infoWrapper = this.productInfoWrapper;
        const infoContainer = this.productInfoContainer;
        const blocks = this.productBlocks;
        const scrollElement = this.productConfiguratorScroll;
        const track = this.productConfiguratorTrack;
        const actions = this.productStickyActions;
        const mediaContainer = this.productMediaContainer;
        const mediaViewer = this.productMediaViewer;

        if (
          !product ||
          !infoWrapper ||
          !infoContainer ||
          !blocks ||
          !scrollElement ||
          !track ||
          !actions ||
          !mediaContainer ||
          !mediaViewer
        ) {
          this.resetProductStickyActions();
          return;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const productRect = product.getBoundingClientRect();
        const mediaContainerRect = mediaContainer.getBoundingClientRect();
        const mediaViewerRect = mediaViewer.getBoundingClientRect();
        const actionsHeight = Math.ceil(actions.getBoundingClientRect().height);
        const stickyTop = Math.max(0, parseFloat(window.getComputedStyle(infoContainer).top) || 0);
        const infoStartOffset = Math.max(0, Math.round(mediaViewerRect.top - mediaContainerRect.top));
        const mediaViewerHeight = Math.ceil(mediaViewerRect.height || mediaViewer.scrollHeight || 0);

        if (viewportHeight <= 0 || actionsHeight <= 0 || mediaViewerHeight <= 0) {
          this.resetDesktopProductStickyActions();
          return;
        }

        const rightColumnHeight = infoStartOffset + Math.max(actionsHeight + 160, mediaViewerHeight);

        // These dimensions define the scroll viewport. Apply changed inputs as
        // one group before measuring it; stable scroll frames need no writes.
        this.setProductStickyStyle(infoContainer, '--product-info-start-offset', `${infoStartOffset}px`);
        this.setProductStickyStyle(infoContainer, '--product-right-column-height', `${rightColumnHeight}px`);
        this.toggleProductStickyClass(blocks, 'is-config-scroll-stage', true);

        const scrollViewportHeight = Math.max(0, Math.floor(scrollElement.getBoundingClientRect().height));
        const trackHeight = Math.ceil(track.scrollHeight || track.getBoundingClientRect().height);
        const scrollDistance = Math.max(0, infoStartOffset + trackHeight - scrollViewportHeight);
        const mediaHeight = Math.ceil(mediaContainer.scrollHeight || mediaContainerRect.height || 0);
        const visibleStageHeight =
          scrollDistance > 0 ? Math.max(rightColumnHeight, viewportHeight - stickyTop) : rightColumnHeight;
        // Keep following sections below the viewport until the configurator track has reached its end.
        const stageHeight = Math.max(visibleStageHeight + scrollDistance, mediaHeight);
        const progress = Math.min(Math.max(stickyTop - productRect.top, 0), scrollDistance);

        this.toggleProductStickyClass(product, 'is-config-scroll-stage', true);
        this.setProductStickyStyle(product, '--product-config-stage-height', `${stageHeight}px`);
        this.setProductStickyStyle(infoWrapper, '--product-config-stage-height', `${stageHeight}px`);
        this.setProductStickyStyle(blocks, '--product-config-track-offset', `${Math.round(progress) * -1}px`);
        this.toggleProductStickyClass(blocks, 'is-config-scroll-complete', scrollDistance > 0 && progress >= scrollDistance - 1);
        this.toggleProductStickyClass(actions, 'is-fixed', false);
        this.toggleProductStickyClass(actions, 'is-anchored', false);
      }

      capturePremiumTabsState() {
        return Array.from(this.querySelectorAll('product-premium-tabs')).map((tabs) => ({
          groupId: tabs.dataset.groupId,
          activeBlockId: tabs.dataset.activeBlockId || '',
        }));
      }

      restorePremiumTabsState(states = []) {
        if (!states.length) return;

        const premiumTabsElements = Array.from(this.querySelectorAll('product-premium-tabs'));
        states.forEach((state) => {
          if (!state.groupId || !state.activeBlockId) return;

          const premiumTabs = premiumTabsElements.find((tabs) => tabs.dataset.groupId === state.groupId);
          premiumTabs?.setActiveByBlockId?.(state.activeBlockId, { force: true });
        });
      }

      updateVariantAwareBlocks(html) {
        let hasUpdatedBlocks = false;
        const destinationBlocks = this.querySelectorAll('[data-updates-on-variant="true"][data-product-block-key]');

        destinationBlocks.forEach((destinationBlock) => {
          const blockKey = destinationBlock.dataset.productBlockKey;
          const sourceBlock = html.querySelector(
            `[data-updates-on-variant="true"][data-product-block-key="${blockKey}"]`,
          );

          if (!sourceBlock) {
            hasUpdatedBlocks = this.syncProductInfoBlock(destinationBlock, null, true) || hasUpdatedBlocks;
            return;
          }

          hasUpdatedBlocks = this.syncProductInfoBlock(destinationBlock, sourceBlock,
            sourceBlock.classList.contains('hidden') || sourceBlock.innerHTML.trim() === '') || hasUpdatedBlocks;
        });

        return hasUpdatedBlocks;
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.teardownProductStickyActions();
        this.variantIntentAbortController?.abort();
        this.variantIntentAbortController = undefined;
        this.cancelProductInfoRequest();
        clearInterval(this.timer);

        if (this.blockPositionHandler) {
          document.removeEventListener('matchMobile', this.blockPositionHandler);
          document.removeEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        clearInterval(this.timer);
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.queueProductStickyActionsUpdate();
            return;
          }

          this.updateMedia(variant);
          const premiumTabsState = this.capturePremiumTabsState();

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              this.syncProductInfoBlock(destination, source, shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('BuyButtons');
          updateSourceFromDestination('QuantityPricingSelector', ({ classList }) => classList.contains('hidden'));
          if (this.updateVariantAwareBlocks(html) && this.productMediaWrapper) {
            this.handleBlocksPosition();
          }
          this.restorePremiumTabsState(premiumTabsState);

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
          );

          this.currentVariant = variant;

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.queueProductStickyActionsUpdate();
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      syncProductInfoBlock(destination, source, hidden = false) {
        const content = source?.innerHTML || '';
        let changed = false;
        if (destination.innerHTML !== content) {
          destination.innerHTML = content;
          changed = true;
        }
        if (destination.classList.contains('hidden') !== hidden) {
          destination.classList.toggle('hidden', hidden);
          changed = true;
        }
        return changed;
      }

      initProductVariantIntent() {
        this.variantIntentAbortController?.abort();
        this.variantIntentAbortController = new AbortController();
        const { signal } = this.variantIntentAbortController;
        const prepare = this.prefetchProductVariantIntent.bind(this);
        this.addEventListener('pointerover', prepare, { passive: true, signal });
        this.addEventListener('focusin', prepare, { signal });
        this.addEventListener('submit', this.guardProductVariantSubmission.bind(this), { capture: true, signal });
        window.addEventListener('pagehide', () => this.cancelProductInfoRequest(), { signal });
      }

      guardProductVariantSubmission(event) {
        if (!this.productVariantSelectionUnresolved) return;
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !this.contains(form) ||
          ![`product-form-${this.dataset.section}`, `product-form-installment-${this.dataset.section}`].includes(form.getAttribute('id'))) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      prefetchProductVariantIntent(event) {
        if (!this.isConnected || event.pointerType === 'touch' || navigator.connection?.saveData ||
          /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
        if (this.productInfoBusyValue !== undefined || (this.productInfoRequest && !this.productInfoRequest.speculative)) return;
        const picker = this.variantSelectors;
        // A deferred size can choose a different available representative. Let
        // the real change resolve it; never guess that option or a sibling product.
        if (!picker || picker.querySelector('[data-deferred-size-option], [data-deferred-size-config]')) return;
        if (!(event.target instanceof Element)) return;
        const input = event.target.closest('input[type="radio"]') || event.target.closest('label')?.control;
        if (!input || input.type !== 'radio' || input.checked || input.disabled ||
          input.closest('variant-selects') !== picker || !input.dataset.optionValueId) return;
        const productUrl = input.dataset.productUrl || this.dataset.url;
        if (productUrl !== this.dataset.url) return;
        const selected = Array.from(picker.querySelectorAll('select option[selected], fieldset input:checked'));
        const position = selected.findIndex(option => option.tagName === 'INPUT' && option.name === input.name);
        if (position < 0 || selected.some(option => !option.dataset.optionValueId)) return;
        const values = selected.map(option => option.dataset.optionValueId);
        values[position] = input.dataset.optionValueId;
        const requestUrl = this.buildRequestUrlWithParams(productUrl, values);
        const key = this.getProductInfoRequestKey(requestUrl);
        const now = Date.now();
        if (this.variantIntentKey === key && now - this.variantIntentAt < 1500) return;
        this.variantIntentKey = key;
        this.variantIntentAt = now;
        this.requestProductInfo(requestUrl, true).promise.catch(() => {});
      }

      getProductInfoRequestKey(requestUrl) {
        return JSON.stringify([
          new URL(requestUrl, document.baseURI).href, this.sectionId, this.dataset.url, this.productId,
          document.documentElement.lang, window.Shopify?.locale, window.Shopify?.country,
          window.Shopify?.currency?.active, window.Shopify?.routes?.root,
        ]);
      }

      requestProductInfo(requestUrl, speculative = false) {
        const key = this.getProductInfoRequestKey(requestUrl);
        const pending = this.productInfoRequest;
        if (pending && pending.key === key && !pending.settled && !pending.controller.signal.aborted &&
          (!pending.speculative || Date.now() - pending.startedAt < 1500)) {
          if (!speculative) {
            pending.speculative = false;
            window.clearTimeout(pending.timer);
          }
          return pending;
        }
        pending?.controller.abort();
        window.clearTimeout(pending?.timer);
        const request = { key, controller: new AbortController(), speculative, startedAt: Date.now(), settled: false };
        this.productInfoRequest = request;
        this.abortController = request.controller;
        if (speculative) request.timer = window.setTimeout(() => request.controller.abort(), 1500);
        request.promise = fetch(requestUrl, { signal: request.controller.signal, cache: 'no-store' })
          .then(response => {
            if (!response.ok) throw new Error(`Product section request failed (${response.status})`);
            return response.text();
          })
          .finally(() => {
            request.settled = true;
            window.clearTimeout(request.timer);
            if (this.productInfoRequest === request) this.productInfoRequest = undefined;
          });
        return request;
      }

      setProductInfoBusy(busy) {
        if (busy) {
          if (this.productInfoBusyValue === undefined) this.productInfoBusyValue = this.getAttribute('aria-busy');
          if (this.getAttribute('aria-busy') !== 'true') this.setAttribute('aria-busy', 'true');
        } else if (this.productInfoBusyValue !== undefined) {
          if (this.productInfoBusyValue === null) this.removeAttribute('aria-busy');
          else this.setAttribute('aria-busy', this.productInfoBusyValue);
          this.productInfoBusyValue = undefined;
        }
      }

      cancelProductInfoRequest() {
        this.productInfoRequestSequence = (this.productInfoRequestSequence || 0) + 1;
        this.abortController?.abort();
        window.clearTimeout(this.productInfoRequest?.timer);
        this.productInfoRequest = undefined;
        this.pendingRequestUrl = null;
        this.setProductInfoBusy(false);
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        const sequence = this.productInfoRequestSequence = (this.productInfoRequestSequence || 0) + 1;
        this.productVariantSelectionUnresolved = true;
        const request = this.requestProductInfo(requestUrl);
        const isCurrent = () => sequence === this.productInfoRequestSequence && !request.controller.signal.aborted && this.isConnected;
        this.setProductInfoBusy(true);
        return request.promise
          .then((responseText) => {
            if (!isCurrent() || request.key !== this.getProductInfoRequestKey(requestUrl)) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (!isCurrent()) return;
            callback(html);
            if (isCurrent()) this.productVariantSelectionUnresolved = false;
            // set focus to last clicked option value
            if (isCurrent()) document.getElementById(targetId)?.focus();
          })
          .catch((error) => {
            if (isCurrent() && error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            if (sequence === this.productInfoRequestSequence) this.setProductInfoBusy(false);
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (!variantSelects) return;

        const currentColorPickerDetails = this.variantSelectors?.querySelector('product-color-variant-selector details');
        const shouldKeepColorPickerOpen = currentColorPickerDetails?.open;

        if (shouldKeepColorPickerOpen) {
          variantSelects.querySelector('product-color-variant-selector details')?.setAttribute('open', '');
        }

        HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const currentUrl = new URL(window.location.href);
        const targetUrl = new URL(url, currentUrl);
        const currentProductPath = currentUrl.pathname.match(/^(\/[^/]+)?\/collections\/[^/]+\/products\/([^/]+)\/?$/);
        const targetProductPath = targetUrl.pathname.match(/^(\/[^/]+)?\/(?:collections\/[^/]+\/)?products\/([^/]+)\/?$/);

        if (
          targetUrl.origin === currentUrl.origin &&
          currentProductPath &&
          targetProductPath &&
          currentProductPath[1] === targetProductPath[1] &&
          currentProductPath[2] === targetProductPath[2]
        ) {
          targetUrl.pathname = currentUrl.pathname;
        }

        if (variantId) targetUrl.searchParams.set('variant', variantId);
        else targetUrl.searchParams.delete('variant');

        const updatedUrl = targetUrl.origin === currentUrl.origin
          ? `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
          : targetUrl.href;
        window.history.replaceState({}, '', updatedUrl);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        clearInterval(this.timer);
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText =
          productForm.querySelector('[data-role="quote-submit-label"]') || productForm.querySelector('[name="add"] > span');
        if (!addButton) return;
        if (addButtonText && !addButtonText.dataset.defaultLabel) {
          addButtonText.dataset.defaultLabel = addButtonText.textContent.trim();
        }

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText) addButtonText.textContent = addButtonText.dataset.defaultLabel || FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}
